"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Download, Monitor, Smartphone, Laptop } from "lucide-react";

// 平台数据结构
interface Platform {
  id: string;
  name: string;
  icon: React.ReactNode;
  downloadUrl: string;
  features: string[];
}

// 平台数据
const platforms: Platform[] = [
  {
    id: "windows",
    name: "Windows",
    icon: <Monitor className="w-6 h-6" />,
    downloadUrl:
      "https://www.clash.la/download-latest.php?url=https://github.com/mihomo-party-org/mihomo-party/releases/download/v1.7.5/mihomo-party-windows-1.7.5-x64-setup.exe",
    features: ["One-click connect", "Kill switch", "DNS leak protection"],
  },
  {
    id: "m",
    name: "macOS M",
    icon: <Laptop className="w-6 h-6" />,
    downloadUrl:
      "https://www.clash.la/download-latest5.php?url=https://github.com/mihomo-party-org/mihomo-party/releases/download/v1.7.3/mihomo-party-catalina-1.7.3-x64.pkg",
    features: ["Native macOS design", "Keychain integration", "Auto-connect"],
  },
  {
    id: "i",
    name: "macOS Intel",
    icon: <Laptop className="w-6 h-6" />,
    downloadUrl:
      "https://www.clash.la/download-latest5.php?url=https://github.com/mihomo-party-org/mihomo-party/releases/download/v1.7.3/mihomo-party-macos-1.7.3-x64.pkg",
    features: ["Native macOS design", "Keychain integration", "Auto-connect"],
  },
  {
    id: "ios",
    name: "iOS",
    icon: <Smartphone className="w-6 h-6" />,
    downloadUrl: "#",
    features: ["Widget support", "Siri shortcuts", "Background protection"],
  },
  {
    id: "android",
    name: "Android",
    icon: <Smartphone className="w-6 h-6" />,
    downloadUrl:
      "https://www.clash.la/download-latest.php?url=https://github.com/MetaCubeX/ClashMetaForAndroid/releases/download/v2.11.7/cmfa-2.11.7-meta-universal-release.apk",
    features: ["Split tunneling", "Always-on VPN", "Ad blocking"],
  },
];

export default function ToolsClient() {
  const [hoveredPlatform, setHoveredPlatform] = useState<string | null>(null);

  return (
    <div>
      {/* Header Section */}
      <motion.div
        className="pt-32 pb-16"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              VPN Tools & Apps
            </motion.h1>
            <motion.p
              className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              Download VPN for all your devices. Protect your privacy and secure
              your connection across Windows, macOS, iOS, and Android.
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Platform Cards */}
      <motion.div
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.6 }}
      >
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {platforms.map((platform, index) => (
            <motion.div
              key={platform.id}
              className="group"
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 + index * 0.2, duration: 0.6 }}
              onMouseEnter={() => setHoveredPlatform(platform.id)}
              onMouseLeave={() => setHoveredPlatform(null)}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg dark:shadow-gray-900/25 border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:shadow-xl dark:hover:shadow-gray-900/40 hover:border-gray-300 dark:hover:border-gray-600 overflow-hidden">
                {/* Platform Header */}
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300">
                    {platform.icon}
                  </div>
                  <div className="ml-6 flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      VPN for {platform.name}
                    </h2>
                    <div className="flex flex-wrap gap-2">
                      {platform.features.map((feature, featureIndex) => (
                        <span
                          key={featureIndex}
                          className="px-3 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full border border-gray-200 dark:border-gray-600"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Content Grid */}
                <div className="gap-8 items-center">
                  {/* Description and Download */}
                  <div className="space-y-6">
                    {/* Download Button */}
                    <motion.button
                      onClick={() => {
                        window.open(platform.downloadUrl, "_blank");
                      }}
                      className="inline-flex items-center bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Download className="w-5 h-5 mr-3" />
                      Download for {platform.name}
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
